import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Loader2, Bookmark, CalendarPlus, Trash2, Info, ChevronRight } from 'lucide-react';

/**
 * ScheduleTemplatesPanel — historical schedule templates for ONE sport.
 *
 * A template is a frozen copy of a past season's games. Creating a schedule from
 * it rolls every date forward by whole weeks (so each fixture keeps its weekday)
 * and writes UNPUBLISHED rows into the games SOT; the Scheduler then sets kickoff
 * times and publishes as usual.
 *
 * API: /api/schedule-templates/{org}/…   Doc: SCHEDULE_TEMPLATES.md
 */
export default function ScheduleTemplatesPanel({ orgId, sport, sportLabel = '', isDark = true }) {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const [templates, setTemplates] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState('');
  const [detail, setDetail] = useState({});       // id -> full template
  const [busy, setBusy] = useState('');

  const [saveOpen, setSaveOpen] = useState(false);
  const [saveSeason, setSaveSeason] = useState('');
  const [saveName, setSaveName] = useState('');

  const [useOpen, setUseOpen] = useState(false);
  const [useTpl, setUseTpl] = useState(null);
  const [targetSeason, setTargetSeason] = useState('');
  const [preview, setPreview] = useState(null);

  const text = isDark ? 'text-white' : 'text-slate-900';
  const muted = isDark ? 'text-slate-400' : 'text-slate-500';
  const dim = isDark ? 'text-slate-500' : 'text-slate-400';
  const cardCls = isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200';
  const inputCls = isDark ? 'bg-slate-800 border-slate-600 text-white' : '';

  const load = useCallback(async () => {
    if (!orgId || !sport) return;
    setLoading(true);
    try {
      const [tRes, sRes] = await Promise.all([
        axios.get(`${API_URL}/api/schedule-templates/${orgId}`, { params: { sport }, withCredentials: true }),
        axios.get(`${API_URL}/api/schedule-templates/${orgId}/source-seasons`, { params: { sport }, withCredentials: true }),
      ]);
      setTemplates(tRes.data?.templates || []);
      setSeasons(sRes.data?.seasons || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load schedule templates.');
    } finally { setLoading(false); }
  }, [orgId, sport, API_URL]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (tpl) => {
    if (expanded === tpl.id) { setExpanded(''); return; }
    setExpanded(tpl.id);
    if (detail[tpl.id]) return;
    try {
      const { data } = await axios.get(`${API_URL}/api/schedule-templates/${orgId}/${tpl.id}`, { withCredentials: true });
      setDetail(p => ({ ...p, [tpl.id]: data }));
    } catch (e) {
      toast.error('Could not load that template.');
    }
  };

  const saveFromSeason = async () => {
    if (!saveSeason) return;
    setBusy('save');
    try {
      await axios.post(`${API_URL}/api/schedule-templates/${orgId}/from-season`,
        { sport, season: saveSeason, name: saveName.trim() || undefined }, { withCredentials: true });
      setSaveOpen(false); setSaveName(''); setSaveSeason('');
      await load();
      toast.success('Season saved as a template.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save that season.');
    } finally { setBusy(''); }
  };

  // Always preview first: the scheduler sees the rolled dates before anything is written.
  const runPreview = async (tpl, season) => {
    setBusy('preview');
    setPreview(null);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/schedule-templates/${orgId}/${tpl.id}/create-schedule`,
        { season, dry_run: true }, { withCredentials: true });
      setPreview(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not preview that season.');
    } finally { setBusy(''); }
  };

  const commit = async () => {
    if (!useTpl || !targetSeason) return;
    setBusy('create');
    try {
      const { data } = await axios.post(
        `${API_URL}/api/schedule-templates/${orgId}/${useTpl.id}/create-schedule`,
        { season: targetSeason }, { withCredentials: true });
      const skipped = (data?.skipped || []).length;
      toast.success(`${data?.games_created || 0} game(s) created for ${targetSeason}` +
        (skipped ? ` · ${skipped} squad(s) skipped` : '') + ' — unpublished, set times then Publish.');
      setUseOpen(false); setPreview(null); setUseTpl(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create that schedule.');
    } finally { setBusy(''); }
  };

  const removeTemplate = async (tpl) => {
    if (!window.confirm(`Delete the template “${tpl.name}”? Any schedule already created from it stays.`)) return;
    try {
      await axios.delete(`${API_URL}/api/schedule-templates/${orgId}/${tpl.id}`, { withCredentials: true });
      await load();
      toast.success('Template deleted.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed.');
    }
  };

  // Offer the next few seasons after the template's source season.
  const targetOptions = (tpl) => {
    const y = tpl?.source_start_year;
    if (!y) return [];
    return [1, 2, 3].map(n => `${y + n}-${y + n + 1}`);
  };

  const sportSeasons = seasons.filter(s => s.sport === sport);

  return (
    <div className="space-y-3" data-testid="schedule-templates-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" variant="outline" onClick={() => setSaveOpen(true)}
          disabled={sportSeasons.length === 0}
          className={`h-7 text-xs ${isDark ? 'border-slate-600 text-slate-300' : ''}`}
          title={sportSeasons.length ? 'Freeze a past season as a reusable template'
                                     : 'No past seasons with games for this sport yet'}
          data-testid="tpl-save-season-btn">
          <Bookmark className="h-3 w-3 mr-1" />Save a season as a template
        </Button>
        {loading && <Loader2 className={`h-3 w-3 animate-spin ${dim}`} />}
        <span className={`text-[11px] ml-auto ${dim}`}>
          {sportLabel ? `${sportLabel} templates` : 'Templates'} — dates roll forward by whole weeks, keeping each weekday.
        </span>
      </div>

      {!loading && templates.length === 0 && (
        <Card className={cardCls}>
          <CardContent className="p-8 text-center">
            <p className={`text-sm ${muted}`}>No schedule templates for this sport yet.</p>
            <p className={`text-xs mt-1 ${dim}`}>
              Use “Save a season as a template” to capture a past season, then build future
              seasons from it.
            </p>
          </CardContent>
        </Card>
      )}

      {templates.map(tpl => {
        const full = detail[tpl.id];
        const open = expanded === tpl.id;
        return (
          <Card key={tpl.id} className={cardCls} data-testid={`tpl-card-${tpl.id}`}>
            <CardContent className="p-3 space-y-2">
              <div className="flex items-start gap-2 flex-wrap">
                <button onClick={() => openDetail(tpl)} className="flex items-start gap-1 text-left"
                  data-testid={`tpl-expand-${tpl.id}`}>
                  <ChevronRight className={`h-4 w-4 mt-0.5 transition-transform ${open ? 'rotate-90' : ''} ${muted}`} />
                  <span>
                    <span className={`text-sm font-semibold ${text}`}>{tpl.name}</span>
                    <span className={`block text-[11px] ${dim}`}>{tpl.description}</span>
                  </span>
                </button>
                <div className="flex items-center gap-2 ml-auto">
                  <Badge variant="outline" className="text-[10px]">{tpl.source_season}</Badge>
                  <Badge variant="outline" className="text-[10px]">{tpl.game_count} games</Badge>
                  <Button size="sm" onClick={() => {
                      setUseTpl(tpl); setPreview(null);
                      const first = targetOptions(tpl)[0] || '';
                      setTargetSeason(first); setUseOpen(true);
                      if (first) runPreview(tpl, first);
                    }}
                    className="h-7 text-xs bg-amber-600 hover:bg-amber-700 text-white"
                    data-testid={`tpl-use-${tpl.id}`}>
                    <CalendarPlus className="h-3 w-3 mr-1" />Create a season
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => removeTemplate(tpl)}
                    className={`h-7 text-xs ${muted}`} title="Delete this template"
                    data-testid={`tpl-delete-${tpl.id}`}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>

              {open && (
                <div className={`text-xs border-t pt-2 ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
                  {!full ? (
                    <span className={dim}><Loader2 className="h-3 w-3 animate-spin inline mr-1" />Loading…</span>
                  ) : (
                    <div className="space-y-1">
                      {(full.squads || []).map(sq => (
                        <div key={sq.slug} className="flex items-center justify-between gap-2">
                          <span className={text}>
                            {sq.slug}
                            {sq.variant ? <span className={muted}> · {sq.variant}</span> : null}
                          </span>
                          <span className={muted}>
                            {sq.game_count} games · {sq.games?.[0]?.date} → {sq.games?.[sq.games.length - 1]?.date}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}

      {/* ── Save a past season as a template ── */}
      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className={`max-w-md ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white'}`}>
          <DialogHeader><DialogTitle className={text}>Save a season as a template</DialogTitle></DialogHeader>
          <div className="space-y-3 py-1" data-testid="tpl-save-dialog">
            <div>
              <Label className={`text-xs ${muted}`}>Season</Label>
              <select value={saveSeason} onChange={e => setSaveSeason(e.target.value)}
                className={`w-full h-8 text-xs rounded-md px-2 border ${isDark ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-slate-300'}`}
                data-testid="tpl-save-season-select">
                <option value="">Choose a season…</option>
                {sportSeasons.map(s => (
                  <option key={s.season} value={s.season}>
                    {s.season} — {s.games} games, {s.squads.length} squad(s)
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label className={`text-xs ${muted}`}>Name (optional)</Label>
              <Input value={saveName} onChange={e => setSaveName(e.target.value)}
                placeholder={saveSeason ? `${saveSeason} ${sportLabel || sport} schedule` : 'Defaults to season + sport'}
                className={inputCls} data-testid="tpl-save-name" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setSaveOpen(false)} className={muted}>Cancel</Button>
              <Button onClick={saveFromSeason} disabled={!saveSeason || busy === 'save'}
                className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="tpl-save-confirm">
                {busy === 'save' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Bookmark className="h-4 w-4 mr-1" />}
                Save template
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Create a season from a template (preview first) ── */}
      <Dialog open={useOpen} onOpenChange={(o) => { setUseOpen(o); if (!o) setPreview(null); }}>
        <DialogContent className={`max-w-2xl ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white'}`}>
          <DialogHeader>
            <DialogTitle className={text}>Create a season from “{useTpl?.name}”</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-1" data-testid="tpl-use-dialog">
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Label className={`text-xs ${muted}`}>Target season</Label>
                <select value={targetSeason}
                  onChange={e => { setTargetSeason(e.target.value); if (useTpl) runPreview(useTpl, e.target.value); }}
                  className={`w-full h-8 text-xs rounded-md px-2 border ${isDark ? 'bg-slate-800 border-slate-600 text-white' : 'bg-white border-slate-300'}`}
                  data-testid="tpl-target-season">
                  {targetOptions(useTpl).map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              {busy === 'preview' && <Loader2 className={`h-4 w-4 animate-spin mb-2 ${dim}`} />}
            </div>

            <div className={`flex items-start gap-2 text-[11px] rounded-md border px-3 py-2 ${
              isDark ? 'border-blue-800 bg-blue-900/20 text-blue-300' : 'border-blue-200 bg-blue-50 text-blue-700'}`}>
              <Info className="h-3.5 w-3.5 mt-[1px] shrink-0" />
              <span>
                Games are created <strong>unpublished</strong> with no kickoff time — set times in the
                grid, then Publish. Dates shift by whole weeks so each fixture keeps its weekday.
                A squad that already has games in the target season is skipped, never doubled.
              </span>
            </div>

            {preview && (
              <div className="space-y-2">
                <div className={`text-xs ${text}`}>
                  <span className="font-semibold">{preview.games_previewed}</span> game(s) would be created ·
                  rolled forward <span className="font-semibold">{preview.years_rolled}</span> season(s)
                </div>
                {(preview.skipped || []).length > 0 && (
                  <div className={`text-[11px] rounded-md border px-3 py-2 ${
                    isDark ? 'border-amber-700/40 bg-amber-900/10 text-amber-300' : 'border-amber-300 bg-amber-50 text-amber-700'}`}
                    data-testid="tpl-preview-skipped">
                    {preview.skipped.map(s => <div key={s.target_slug}>{s.target_slug} — {s.reason}</div>)}
                  </div>
                )}
                <div className={`max-h-56 overflow-y-auto rounded-md border text-[11px] ${isDark ? 'border-slate-700' : 'border-slate-200'}`}
                  data-testid="tpl-preview-rows">
                  {(preview.preview || []).map((g, i) => (
                    <div key={i} className={`flex items-center gap-2 px-2 py-1 ${i % 2 ? (isDark ? 'bg-slate-800/40' : 'bg-slate-50') : ''}`}>
                      <span className={`${muted} w-44 shrink-0`}>{g.squad}</span>
                      <span className={dim}>{g.from}</span>
                      <ChevronRight className="h-3 w-3 opacity-50" />
                      <span className={text}>{g.to}</span>
                      <span className={`${muted} ml-auto truncate`}>{g.is_home ? 'vs' : '@'} {g.opponent}</span>
                    </div>
                  ))}
                  {(preview.preview || []).length === 0 && (
                    <div className={`px-2 py-2 ${dim}`}>Nothing to create for this season.</div>
                  )}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setUseOpen(false)} className={muted}>Cancel</Button>
              <Button onClick={commit}
                disabled={busy === 'create' || !preview || !preview.games_previewed}
                className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="tpl-create-confirm">
                {busy === 'create' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CalendarPlus className="h-4 w-4 mr-1" />}
                Create {preview?.games_previewed || 0} game(s)
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
